"use client";
export const dynamic = "force-dynamic";

import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  Bot,
  BarChart3,
  MessageSquare,
  Workflow,
  ShieldCheck,
  Globe2,
  Sparkles,
  Check,
  ArrowLeft,
} from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import PageSkeleton from "@/components/site/PageSkeleton";

const FEATURES = [
  {
    icon: Bot,
    title: "پاسخگوی هوشمند خودکار",
    desc: "ربات گفت‌وگوی مبتنی بر هوش مصنوعی که ۲۴ ساعته به مشتریان شما در دایرکت، واتساپ و چت سایت پاسخ می‌دهد.",
    bullets: ["یادگیری از مکالمات قبلی", "تنظیم لحن و شخصیت", "انتقال هوشمند به اپراتور انسانی"],
    color: "from-primary-main to-primary-300",
  },
  {
    icon: BarChart3,
    title: "آنالیز پیشرفته رفتار مشتری",
    desc: "داشبوردهای تعاملی برای دیدن مسیر مشتری از اولین کلیک تا خرید، با شناسایی نقاط ریزش.",
    bullets: ["گزارش لحظه‌ای", "تحلیل کوهورت", "خروجی Excel و PDF"],
    color: "from-secondary-main to-secondary-300",
  },
  {
    icon: Workflow,
    title: "اتوماسیون بازاریابی",
    desc: "سناریوهای چندمرحله‌ای بسازید تا پیام، تخفیف یا یادآور به مشتری مناسب در زمان مناسب برسد.",
    bullets: ["تریگرهای رویدادمحور", "تست A/B", "زمان‌بندی هوشمند"],
    color: "from-purple to-blue",
  },
  {
    icon: MessageSquare,
    title: "صندوق پیام یکپارچه",
    desc: "همه پیام‌های اینستاگرام، واتساپ، تلگرام و وبسایت را در یک Inbox مشترک تیمی مدیریت کنید.",
    bullets: ["برچسب‌گذاری هوشمند", "اختصاص خودکار", "یادداشت‌های داخلی تیم"],
    color: "from-teal to-light-blue",
  },
  {
    icon: ShieldCheck,
    title: "امنیت سازمانی",
    desc: "احراز هویت دومرحله‌ای، رمزنگاری end-to-end و کنترل دسترسی نقش‌محور برای آرامش خاطر شما.",
    bullets: ["2FA و SSO", "گزارش لاگ کامل", "استانداردهای ISO"],
    color: "from-emerald-500 to-teal-500",
  },
  {
    icon: Globe2,
    title: "چندزبانه و چندفروشگاهی",
    desc: "از یک حساب کاربری چند فروشگاه با زبان و واحد پولی متفاوت را به‌صورت همزمان مدیریت کنید.",
    bullets: ["پشتیبانی RTL/LTR", "تبدیل ارز خودکار", "تم اختصاصی هر فروشگاه"],
    color: "from-orange to-secondary-main",
  },
];

export default function FeaturesPage() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 900);
    return () => clearTimeout(t);
  }, []);

  if (!ready) return <PageSkeleton />;

  return (
    <SiteShell>
      {/* Hero */}
      <section className="mx-auto max-w-6xl px-6 pt-16 pb-12 sm:pt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2">
            <Sparkles size={14} className="text-primary-main" />
            <span className="text-[12px] font-bold text-muted-foreground">
              همه چیزی که برای رشد نیاز دارید
            </span>
          </div>
          <h1 className="mb-5 text-4xl font-black leading-[1.15] tracking-tight sm:text-5xl lg:text-6xl">
            قدرت یک تیم کامل
            <span className="block brand-gradient-text">در یک پلتفرم</span>
          </h1>
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            ترندی اسمارت ابزارهایی را که قبلاً پراکنده استفاده می‌کردید، در یک تجربهٔ یکپارچه جمع
            کرده — تا کمتر بدوید و بیشتر بفروشید.
          </p>
        </motion.div>
      </section>

      {/* Features grid */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.article
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.05, duration: 0.5 }}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary-main/40"
              >
                <div
                  className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${f.color} text-white shadow-lg`}
                >
                  <Icon size={20} />
                </div>
                <h3 className="mb-2 text-[17px] font-black tracking-tight text-foreground">
                  {f.title}
                </h3>
                <p className="mb-4 text-[13px] leading-relaxed text-muted-foreground">{f.desc}</p>
                <ul className="space-y-2">
                  {f.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-center gap-2 text-[12.5px] text-foreground/80"
                    >
                      <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-primary-main/15 text-primary-main">
                        <Check size={10} strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* Deep dive sections with screenshot mocks */}
      <section className="mx-auto max-w-6xl px-6 pb-24 space-y-20">
        {FEATURES.slice(0, 3).map((f, idx) => {
          const Icon = f.icon;
          const reverse = idx % 2 === 1;
          return (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className={`grid items-center gap-8 lg:grid-cols-2 ${reverse ? "lg:[&>div:first-child]:order-last" : ""}`}
            >
              <div>
                <div
                  className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${f.color} text-white`}
                >
                  <Icon size={18} />
                </div>
                <h2 className="mb-3 text-2xl font-black tracking-tight text-foreground sm:text-3xl">
                  {f.title}
                </h2>
                <p className="mb-5 text-[14px] leading-relaxed text-muted-foreground">{f.desc}</p>
                <ul className="space-y-3">
                  {f.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-primary-main/15 text-primary-main">
                        <Check size={12} strokeWidth={3} />
                      </span>
                      <span className="text-[13.5px] text-foreground/85">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Screenshot mock */}
              <div className="relative">
                <div className="absolute -inset-6 rounded-3xl bg-gradient-to-br from-primary-main/10 via-transparent to-secondary-main/10 blur-2xl" />
                <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-2xl shadow-primary-main/10">
                  {/* Window chrome */}
                  <div className="flex items-center gap-1.5 border-b border-border bg-muted/40 px-4 py-2.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
                    <span className="ml-3 text-[10px] font-mono text-muted-foreground">
                      app.trendi.smart/{f.title.split(" ")[0]}
                    </span>
                  </div>
                  <div className="space-y-3 p-5">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="h-3 w-32 rounded bg-foreground/10" />
                        <div className="h-2 w-24 rounded bg-foreground/5" />
                      </div>
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br ${f.color} text-white`}
                      >
                        <Icon size={14} />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {[0, 1, 2].map((k) => (
                        <div key={k} className="rounded-lg border border-border bg-background p-3">
                          <div className="mb-2 h-2 w-10 rounded bg-foreground/10" />
                          <div className="h-4 w-14 rounded bg-foreground/15" />
                        </div>
                      ))}
                    </div>
                    <div className="rounded-xl border border-border bg-background p-3">
                      <div className="mb-3 flex items-end gap-1.5 h-24">
                        {[40, 65, 35, 80, 55, 92, 70, 48, 88, 60].map((h, k) => (
                          <div
                            key={k}
                            className={`flex-1 rounded-t bg-gradient-to-t ${f.color} opacity-90`}
                            style={{ height: `${h}%` }}
                          />
                        ))}
                      </div>
                      <div className="flex justify-between">
                        <div className="h-2 w-12 rounded bg-foreground/10" />
                        <div className="h-2 w-8 rounded bg-foreground/10" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-4xl px-6 pb-24">
        <div className="relative overflow-hidden rounded-3xl border border-primary-main/30 bg-gradient-to-br from-primary-main/10 via-card to-secondary-main/10 p-10 text-center">
          <h2 className="mb-3 text-2xl font-black tracking-tight text-foreground sm:text-3xl">
            آماده‌اید رشد را شروع کنید؟
          </h2>
          <p className="mx-auto mb-6 max-w-md text-[14px] text-muted-foreground">
            ۱۴ روز رایگان امتحان کنید. خدمات فراتر از تصور.
          </p>
          <a
            href="/login"
            className="inline-flex items-center gap-2 rounded-xl bg-primary-main px-6 py-3 text-[14px] font-black text-white shadow-lg shadow-primary-main/30 transition-colors hover:bg-primary-400"
          >
            شروع رایگان
            <ArrowLeft size={15} />
          </a>
        </div>
      </section>
    </SiteShell>
  );
}
